import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Flame, Users, Check, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from '../hooks/use-toast';
import api from '../lib/api';

/** Next Liberation Night Vigil = 2nd Friday of the current month at 23:00 UTC. */
function nextSecondFridayUTC(now = new Date()) {
  const build = (year, month) => {
    const first = new Date(Date.UTC(year, month, 1));
    const firstDow = first.getUTCDay(); // 0=Sun ... 5=Fri
    const offsetToFriday = (5 - firstDow + 7) % 7;
    const day = 1 + offsetToFriday + 7; // 2nd Friday
    return new Date(Date.UTC(year, month, day, 23, 0, 0));
  };
  let target = build(now.getUTCFullYear(), now.getUTCMonth());
  if (target.getTime() <= now.getTime()) {
    const nextMonth = now.getUTCMonth() + 1;
    const year = now.getUTCFullYear() + Math.floor(nextMonth / 12);
    target = build(year, nextMonth % 12);
  }
  return target;
}

function splitDiff(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return { days, hours, minutes, seconds };
}

const pad = (n) => String(n).padStart(2, '0');

const isoDate = (d) =>
  `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;

const rsvpStorageKey = (iso) => `dogm_vigil_rsvp_${iso}`;

export default function VigilCountdown() {
  const [target, setTarget] = useState(() => nextSecondFridayUTC());
  const [remaining, setRemaining] = useState(() => splitDiff(target.getTime() - Date.now()));
  const vigilIso = useMemo(() => isoDate(target), [target]);
  const [rsvpOpen, setRsvpOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    try {
      setDone(!!localStorage.getItem(rsvpStorageKey(vigilIso)));
    } catch (_) { /* ignore */ }
  }, [vigilIso]);

  const refreshCount = useCallback(() => {
    api.vigilRsvpCount(vigilIso).then(r => setCount(r.count || 0)).catch(() => {});
  }, [vigilIso]);

  useEffect(() => { refreshCount(); }, [refreshCount]);

  useEffect(() => {
    const tick = () => {
      const now = Date.now();
      let diff = target.getTime() - now;
      if (diff <= 0) {
        const next = nextSecondFridayUTC(new Date(now));
        setTarget(next);
        diff = next.getTime() - now;
      }
      setRemaining(splitDiff(diff));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.rsvpVigil({ vigil_date: vigilIso, name, email });
      setCount(r.count || 0);
      setDone(true);
      try { localStorage.setItem(rsvpStorageKey(vigilIso), '1'); } catch (_) { /* ignore */ }
      setRsvpOpen(false);
      setName(''); setEmail('');
      toast({ title: 'You\u2019re in!', description: 'See you at the vigil. We\u2019ll be praying for you.' });
    } catch (err) {
      toast({ title: 'Could not save RSVP', description: String(err?.response?.data?.detail || err.message) });
    } finally { setBusy(false); }
  };

  const dateLabel = target.toLocaleDateString(undefined, {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
  });

  return (
    <section className="relative py-16 md:py-20 overflow-hidden bg-[#0B1B27]">
      <div
        aria-hidden
        className="absolute inset-0 opacity-30"
        style={{
          background:
            'radial-gradient(circle at 20% 10%, rgba(217,164,38,0.35), transparent 55%), radial-gradient(circle at 80% 90%, rgba(26,110,100,0.45), transparent 55%)',
        }}
      />
      <div className="relative max-w-6xl mx-auto px-4 md:px-6 grid lg:grid-cols-2 gap-10 items-center text-white">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold-brand/15 border border-gold-brand/30 text-gold-brand text-xs tracking-[0.3em] uppercase font-semibold">
            <Flame className="h-3.5 w-3.5" /> Monthly Vigil
          </div>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-tight mt-5">
            Liberation Night <span className="italic text-gold-brand">Vigil</span>
          </h2>
          <p data-testid="vigil-target-date" className="mt-4 text-base md:text-lg text-white/75 max-w-xl">
            Next gathering &middot; <span className="font-semibold text-white">{dateLabel}</span> &middot; 11:00 PM GMT &mdash; a night of worship, Word, and travailing prayer.
          </p>

          <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm">
            <Users className="h-4 w-4 text-gold-brand" />
            <span data-testid="vigil-rsvp-count" className="tabular-nums font-semibold text-white">{count}</span>
            <span className="text-white/70">{count === 1 ? 'saint is joining' : 'saints are joining'} this month</span>
          </div>

          {!rsvpOpen && (
            <div className="mt-6 flex flex-wrap gap-3">
              {done ? (
                <Button data-testid="vigil-rsvp-done" disabled className="bg-teal-brand/90 text-white font-semibold h-11 px-6 cursor-default">
                  <Check className="mr-2 h-4 w-4" /> You&rsquo;re in
                </Button>
              ) : (
                <Button data-testid="vigil-rsvp-open" onClick={() => setRsvpOpen(true)} className="bg-gold-brand hover:bg-[#C48A1F] text-ink font-semibold h-11 px-6">
                  I&rsquo;ll be there
                </Button>
              )}
              <Link to="/prayer">
                <Button variant="outline" className="h-11 px-6 border-white/40 text-white hover:bg-white hover:text-ink">Send a Prayer Point</Button>
              </Link>
            </div>
          )}

          {rsvpOpen && !done && (
            <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-xl">
              <Input
                data-testid="vigil-rsvp-name"
                placeholder="Your name (optional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="bg-white/10 border-white/20 text-white placeholder:text-white/50 h-11"
              />
              <Input
                data-testid="vigil-rsvp-email"
                type="email"
                placeholder="Email (optional)"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-white/10 border-white/20 text-white placeholder:text-white/50 h-11"
              />
              <Button data-testid="vigil-rsvp-submit" type="submit" disabled={busy} className="bg-gold-brand hover:bg-[#C48A1F] text-ink font-semibold h-11 px-5 whitespace-nowrap">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Confirm
              </Button>
            </form>
          )}
        </div>

        <div data-testid="vigil-countdown" className="grid grid-cols-4 gap-3 md:gap-4">
          {[
            { label: 'Days', value: remaining.days },
            { label: 'Hours', value: pad(remaining.hours) },
            { label: 'Minutes', value: pad(remaining.minutes) },
            { label: 'Seconds', value: pad(remaining.seconds) },
          ].map((seg) => (
            <div
              key={seg.label}
              className="relative rounded-2xl bg-white/5 backdrop-blur border border-white/10 p-4 md:p-6 text-center"
            >
              <div className="font-serif-display text-4xl md:text-6xl tabular-nums text-gold-brand leading-none">{seg.value}</div>
              <div className="mt-2 text-[10px] md:text-xs tracking-[0.3em] uppercase text-white/60">{seg.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
